import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  MessageSquare, 
  Trash2, 
  Clock, 
  Brain, 
  User,
  ChevronLeft,
  Search,
  Sparkles,
  BookOpen,
  Heart,
  Loader2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import api from "../lib/api";
import { cn } from "../lib/utils";
import { useLanguage } from "../context/LanguageContext";

export default function HistoryPage() {
  const navigate = useNavigate();
  const { t, isRTL } = useLanguage();
  const [conversations, setConversations] = useState([]);
  const [memory, setMemory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("conversations");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [convRes, memRes] = await Promise.all([
        api.get("/conversations?limit=1000"), // جلب كل المحادثات
        api.get("/memory")
      ]);
      setConversations(convRes.data.conversations || []);
      setMemory(memRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("خطأ في تحميل البيانات");
    } finally {
      setLoading(false);
    }
  };

  const deleteConversation = async (sessionId, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/chat/session/${sessionId}`);
      setConversations(prev => prev.filter(c => c.id !== sessionId));
      toast.success("تم حذف المحادثة");
    } catch (error) {
      toast.error("خطأ في حذف المحادثة");
    }
  };

  const clearMemory = async () => {
    if (!window.confirm("هل أنت متأكد من مسح جميع المحادثات والذاكرة؟\n\nسيتم حذف:\n- جميع المحادثات\n- جميع الرسائل\n- الذاكرة المحفوظة\n\nهذا الإجراء لا يمكن التراجع عنه!")) {
      return;
    }
    try {
      const response = await api.delete("/conversations/all");
      console.log("All cleared:", response.data);
      setMemory(null);
      setConversations([]);
      toast.success(`تم مسح ${response.data.deleted_sessions} محادثة و ${response.data.deleted_messages} رسالة`);
    } catch (error) {
      console.error("Clear all error:", error);
      toast.error("خطأ في مسح المحادثات");
    }
  };

  // ترتيب المحادثات من الأحدث للأقدم
  const filteredConversations = conversations
    .filter(conv => conv.title?.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      const dateA = new Date(a.updated_at || a.created_at || 0);
      const dateB = new Date(b.updated_at || b.created_at || 0);
      return dateB - dateA; // الأحدث أولاً
    });

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) return "اليوم";
    if (days === 1) return "أمس";
    if (days < 7) return `منذ ${days} أيام`;
    
    // التاريخ الميلادي بالأرقام الإنجليزية
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="h-screen bg-background flex flex-col" data-testid="history-page">
      {/* Header */}
      <div className="px-4 py-4 pt-20 md:pt-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-bold text-foreground">{isRTL ? "السجل والذاكرة" : "History & Memory"}</h1>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="rounded-lg"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2">
          <Button
            variant={activeTab === "conversations" ? "default" : "ghost"}
            onClick={() => setActiveTab("conversations")}
            className="flex-1 gap-2"
            data-testid="tab-conversations"
          >
            <MessageSquare className="w-4 h-4" />
            <span>المحادثات</span>
            <span className="text-xs opacity-70">({conversations.length})</span>
          </Button>
          <Button
            variant={activeTab === "memory" ? "default" : "ghost"}
            onClick={() => setActiveTab("memory")}
            className="flex-1 gap-2"
            data-testid="tab-memory"
          >
            <Brain className="w-4 h-4" />
            <span>الذاكرة</span>
          </Button>
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4">
          <AnimatePresence mode="wait">
            {activeTab === "conversations" ? (
              <motion.div
                key="conversations"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
              >
                {/* Search */}
                <div className="relative mb-4">
                  <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="ابحث في المحادثات..."
                    className="w-full pl-4 pr-10 py-2.5 bg-secondary rounded-xl text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-primary/30"
                    data-testid="search-input"
                  />
                </div>

                {/* Conversations List */}
                {filteredConversations.length === 0 ? (
                  <div className="text-center py-12">
                    <Sparkles className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" />
                    <p className="text-muted-foreground">لا توجد محادثات</p>
                    <Button
                      onClick={() => navigate("/chat")}
                      className="mt-4"
                    >
                      ابدأ محادثة جديدة
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredConversations.map((conv, idx) => (
                      <motion.div
                        key={conv.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.03 }}
                        onClick={() => navigate(`/chat/${conv.id}`)}
                        className="group p-4 bg-card hover:bg-secondary/50 rounded-xl border border-border cursor-pointer transition-all"
                        data-testid={`conversation-${conv.id}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-foreground truncate mb-1">
                              {conv.title || "محادثة بدون عنوان"}
                            </h3>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {formatDate(conv.updated_at || conv.created_at)}
                              </span>
                              {conv.message_count > 0 && (
                                <span className="flex items-center gap-1">
                                  <MessageSquare className="w-3 h-3" />
                                  {conv.message_count} رسالة
                                </span>
                              )}
                              {conv.assistant_id && conv.assistant_id !== "general" && (
                                <span className="px-2 py-0.5 bg-primary/10 text-primary rounded-full">
                                  {conv.assistant_id}
                                </span>
                              )}
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => deleteConversation(conv.id, e)}
                            className="opacity-0 group-hover:opacity-100 h-8 w-8 rounded-lg hover:bg-destructive/10"
                            data-testid={`delete-${conv.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </motion.div>
            ) : (
              <motion.div
                key="memory"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                {/* Memory Profile */}
                {memory ? (
                  <div className="space-y-4">
                    {/* User Info */}
                    <div className="p-4 bg-card rounded-xl border border-border">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                          <User className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-bold text-foreground">
                            {memory.name || "مستخدم HELCOO"}
                          </h3>
                          <p className="text-xs text-muted-foreground">
                            منذ {formatDate(memory.created_at)}
                          </p>
                        </div>
                      </div>

                      {/* Preferences */}
                      {memory.preferences && (
                        <div className="flex flex-wrap gap-2">
                          <span className="px-3 py-1 bg-secondary rounded-full text-xs text-muted-foreground">
                            اللغة: {memory.preferences.language === "ar" ? "العربية" : "English"}
                          </span>
                          <span className="px-3 py-1 bg-secondary rounded-full text-xs text-muted-foreground">
                            الأسلوب: {memory.preferences.response_style === "balanced" ? "متوازن" : memory.preferences.response_style}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Interests */}
                    {memory.interests && memory.interests.length > 0 && (
                      <div className="p-4 bg-card rounded-xl border border-border">
                        <div className="flex items-center gap-2 mb-3">
                          <Heart className="w-4 h-4 text-pink-500" />
                          <h4 className="font-semibold text-foreground">الاهتمامات</h4>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {memory.interests.map((interest, idx) => (
                            <span
                              key={`interest-${idx}-${interest}`}
                              className="px-3 py-1.5 bg-pink-500/10 text-pink-500 rounded-full text-sm"
                            >
                              {interest}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Conversation Summaries */}
                    {memory.conversation_summaries && memory.conversation_summaries.length > 0 && (
                      <div className="p-4 bg-card rounded-xl border border-border">
                        <div className="flex items-center gap-2 mb-3">
                          <BookOpen className="w-4 h-4 text-blue-500" />
                          <h4 className="font-semibold text-foreground">
                            ملخصات المحادثات ({memory.conversation_summaries.length})
                          </h4>
                        </div>
                        <div className="space-y-2">
                          {memory.conversation_summaries.slice(-5).reverse().map((summary, idx) => (
                            <div
                              key={`summary-${idx}-${summary.summary?.slice(0,10)}`}
                              className="p-3 bg-secondary/50 rounded-lg text-sm"
                            >
                              <p className="text-foreground">{summary.summary}</p>
                              {summary.topics && summary.topics.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {summary.topics.slice(0, 3).map((topic, tidx) => (
                                    <span
                                      key={`topic-${tidx}-${topic}`}
                                      className="px-2 py-0.5 bg-blue-500/10 text-blue-500 rounded text-xs"
                                    >
                                      {topic}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Facts */}
                    {memory.facts && memory.facts.length > 0 && (
                      <div className="p-4 bg-card rounded-xl border border-border">
                        <div className="flex items-center gap-2 mb-3">
                          <Sparkles className="w-4 h-4 text-yellow-500" />
                          <h4 className="font-semibold text-foreground">حقائق محفوظة</h4>
                        </div>
                        <ul className="space-y-2">
                          {memory.facts.slice(-5).map((fact, idx) => (
                            <li
                              key={`fact-${idx}-${typeof fact === 'string' ? fact.slice(0,10) : fact.fact?.slice(0,10)}`}
                              className="flex items-start gap-2 text-sm text-muted-foreground"
                            >
                              <span className="text-yellow-500 mt-1">•</span>
                              <span>{fact.fact || fact}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Clear Memory Button */}
                    <Button
                      variant="outline"
                      onClick={clearMemory}
                      className="w-full text-destructive hover:bg-destructive/10 border-destructive/30"
                      data-testid="clear-memory-btn"
                    >
                      <Trash2 className="w-4 h-4 ml-2" />
                      مسح الذاكرة
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Brain className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" />
                    <p className="text-muted-foreground mb-2">لا توجد ذاكرة محفوظة</p>
                    <p className="text-xs text-muted-foreground">
                      ستتذكرك HELCOO تلقائياً من خلال محادثاتك
                    </p>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </ScrollArea>
    </div>
  );
}
